/**
 * Dates that came out of a `date` column.
 *
 * PostgreSQL sends a `date` as "2026-09-14", and `new Date("2026-09-14")`
 * parses that as **midnight UTC** — which, anywhere west of Greenwich, is the
 * evening of the day before. Rendered through `toLocaleDateString()` in New
 * York it reads "Sep 13, 2026". Every date on every screen was a day early.
 *
 * It hid because it is only ever off by one and always in the same direction,
 * so nothing looks corrupt — it looks like a date. It surfaced when a flag
 * showed "in 3 days · Sep 13" beside a date three days away, because the day
 * count is computed by the database and the date string was not, and the two
 * disagreed in public.
 *
 * This is not cosmetic here. These are hearing dates, commission dates and
 * statutory deadlines, and a report filed against the wrong day is the kind of
 * mistake this system exists to prevent.
 *
 * A timestamptz is a real instant and must NOT go through these — it is
 * already unambiguous, and re-reading it as a local calendar date would
 * introduce the error these avoid.
 */

/** Parse "YYYY-MM-DD" as that calendar day in the viewer's own timezone. */
export function parseDateOnly(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return new Date(value);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** "14 Sep 2026", in the viewer's locale. */
export function formatDate(value: string): string {
  return parseDateOnly(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
